import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import { downloadKarkhanaReport, getKarkhanaReport } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { hours } from "./punches/punchModel";
import { rupees, todayIso } from "./rates/rateModel";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import PageHeader from "./ui/PageHeader";
import { kp } from "./karkhanaPaths";
import { useAddressParam } from "./ui/addressState";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const monthStart = () => `${todayIso().slice(0, 8)}01`;

type Tab = "people" | "designs" | "looms";

/** Studio admin: what each person earned, each design and each loom did, for chosen dates; Excel download. */
const KarkhanaReportsPage = () => {
  const history = useHistory();
  const role = readRole();
  // Dates and tab are in the address (replaced), so a reload or a shared link shows the same report.
  const [from, setFrom] = useAddressParam("from", monthStart());
  const [to, setTo] = useAddressParam("to", todayIso());
  const [tabParam, setTab] = useAddressParam("tab", "people");
  const tab: Tab = (["people", "designs", "looms"] as string[]).includes(tabParam) ? (tabParam as Tab) : "people";
  const [report, setReport] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setReport(null);
    try {
      const res: any = await getKarkhanaReport(from, to);
      if (res?.success) setReport(res.data);
      else notifications({ title: "Error", message: res?.message || "Could not make the report", type: "danger" });
    } catch {
      notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    }
  }, [from, to]);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    load();
  }, [role, history, load]);

  const excel = async () => {
    setBusy(true);
    try {
      await downloadKarkhanaReport(from, to);
    } catch (e: any) {
      notifications({ title: "Error", message: e?.message || "Could not make the Excel file", type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  if (!canManageKarkhana(role)) return null;
  const workTypes: string[] = report?.workTypes || [];

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Karkhana reports" back={kp.more()} />
      <div className="kw-toolbar">
        <input type="date" className="form-control" style={{ width: "auto" }} value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} aria-label="From" />
        <span className="kw-muted">to</span>
        <input type="date" className="form-control" style={{ width: "auto" }} value={to} min={from} max={todayIso()} onChange={(e) => e.target.value && setTo(e.target.value)} aria-label="To" />
        <Button className="ResetBtn" disabled={busy || !report} onClick={excel}>
          {busy ? "Making Excel…" : "Download Excel"}
        </Button>
      </div>

      {report ? (
        <p className="kw-lede">
          Total pay <strong>{rupees(report.totalPay)}</strong>
          {report.waitingEntries ? ` · not included: ${report.waitingEntries} entries waiting for approval (${rupees(report.waitingPay)})` : ""}
        </p>
      ) : (
        <div className="kw-empty">Loading…</div>
      )}

      <div className="kr-chips" role="tablist" style={{ marginBottom: 10 }}>
        {(["people", "designs", "looms"] as Tab[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`kr-choice${tab === k ? " is-on" : ""}`} onClick={() => setTab(k)}>
            {k === "people" ? "People" : k === "designs" ? "Designs" : "Looms"}
          </button>
        ))}
      </div>

      {report && tab === "people" ? (
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th className="text-end">Days</th>
                {workTypes.map((w) => (
                  <th key={w} className="text-end">
                    {w} in
                  </th>
                ))}
                <th className="text-end">Weaving</th>
                <th className="text-end">Alone</th>
                <th className="text-end">Incentive</th>
                <th className="text-end">Time</th>
                <th className="text-end">Time pay</th>
                <th className="text-end">Job work</th>
                <th className="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              {(report.people || []).map((p: any) => (
                <tr key={p.personId}>
                  <td className="kw-code">{p.code}</td>
                  <td>{p.name}</td>
                  <td className="text-end">{p.days}</td>
                  {workTypes.map((w) => (
                    <td key={w} className="text-end">
                      {p.inches?.[w] ? Number(p.inches[w]) : ""}
                    </td>
                  ))}
                  <td className="text-end">{rupees(p.weaving, 0)}</td>
                  <td className="text-end">{Number(p.aloneAllowance) ? rupees(p.aloneAllowance, 0) : ""}</td>
                  <td className="text-end">{Number(p.incentive) ? rupees(p.incentive, 0) : ""}</td>
                  <td className="text-end">{p.timeMinutes ? hours(p.timeMinutes) : ""}</td>
                  <td className="text-end">{Number(p.timePay) ? rupees(p.timePay, 0) : ""}</td>
                  <td className="text-end">{Number(p.jobPay) ? rupees(p.jobPay, 0) : ""}</td>
                  <td className="text-end">
                    <strong>{rupees(p.total, 0)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {report && tab === "designs" ? (
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Design</th>
              <th className="text-end">Sarees</th>
              <th className="text-end">Inches</th>
              <th className="text-end">Loom pay</th>
            </tr>
          </thead>
          <tbody>
            {(report.designs || []).map((d: any) => (
              <tr key={d.designNumber}>
                <td className="kw-code">{d.designNumber}</td>
                <td className="text-end">{d.sarees}</td>
                <td className="text-end">{Number(d.inches)}</td>
                <td className="text-end">{rupees(d.loomPay, 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {report && tab === "looms" ? (
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Loom</th>
              <th>Group</th>
              <th className="text-end">Days worked</th>
              <th className="text-end">Days closed</th>
              <th className="text-end">Inches</th>
            </tr>
          </thead>
          <tbody>
            {(report.looms || []).map((l: any) => (
              <tr key={l.loomCode}>
                <td className="kw-code">{l.loomCode}</td>
                <td>{l.loomGroup || ""}</td>
                <td className="text-end">{l.daysWorked}</td>
                <td className="text-end">{l.daysClosed || ""}</td>
                <td className="text-end">{Number(l.inches) || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
};

export default KarkhanaReportsPage;
